<template>
  <div class="fireworks-page">
    <!-- 头部 -->
    <header class="page-header">
      <h1>{{ shopInfo.name }}</h1>
      <p class="shop-desc">{{ shopInfo.description }}</p>
      <p class="contact">
        <i class="el-icon-phone"></i>
        <span class="phone-number" @click="handlePhone(shopInfo.phone1)">{{ shopInfo.phone1 }}</span>
        <span class="phone-divider">/</span>
        <span class="phone-number" @click="handlePhone(shopInfo.phone2)">{{ shopInfo.phone2 }}</span>
      </p>
      <p class="address">
        <i class="el-icon-location"></i> {{ shopInfo.address }}
      </p>
    </header>

    <!-- 分类筛选 -->
    <div class="category-filter">
      <div
        class="filter-item"
        :class="{ active: currentCategory === '' }"
        @click="currentCategory = ''"
      >全部</div>
      <div
        v-for="cat in categories"
        :key="cat"
        class="filter-item"
        :class="{ active: currentCategory === cat }"
        @click="currentCategory = cat"
      >{{ cat }}</div>
    </div>

    <!-- 搜索框 -->
    <div class="search-box">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索产品名称..."
        prefix-icon="el-icon-search"
        clearable
        size="small"
      ></el-input>
    </div>

    <!-- 产品数量统计 -->
    <div class="product-count">
      共 <span>{{ filteredProducts.length }}</span> 款产品
    </div>

    <!-- 产品列表 -->
    <div class="product-list">
      <div
        v-for="item in filteredProducts"
        :key="item.id"
        class="product-card"
      >
        <!-- 实物图 -->
        <div class="product-image" @click="previewImage(item)">
          <img
            :src="getProductImage(item)"
            :alt="item.name"
            @error="handleImageError($event)"
          />
          <div class="image-overlay" v-if="item.image">
            <i class="el-icon-zoom-in"></i>
          </div>
        </div>

        <!-- 产品信息 -->
        <div class="product-info">
          <div class="product-header">
            <span class="product-category">{{ item.category }}</span>
            <span class="product-price">¥{{ item.price }}</span>
          </div>
          <h3 class="product-name">{{ item.name }}</h3>
          <p class="product-spec">规格: {{ item.spec }}</p>
          <p class="product-remark" v-if="item.remark">{{ item.remark }}</p>

          <div class="product-actions">
            <el-button
              v-if="hasVideo(item)"
              type="primary"
              size="mini"
              icon="el-icon-video-play"
              @click="playVideo(item)"
            >查看效果</el-button>
            <span v-else class="no-video">暂无视频</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 视频弹窗 -->
    <el-dialog
      :title="currentProduct.name + ' - 烟花效果'"
      :visible.sync="videoDialogVisible"
      :width="dialogWidth"
      :fullscreen="isMobile"
      :before-close="closeVideo"
      custom-class="video-dialog"
    >
      <div class="video-container">
        <iframe
          v-if="videoDialogVisible && currentProduct.videoUrl"
          :src="currentProduct.videoUrl"
          frameborder="0"
          allowfullscreen
          class="video-iframe"
        ></iframe>
      </div>
    </el-dialog>

    <!-- 图片预览弹窗 -->
    <el-dialog
      :title="currentProduct.name"
      :visible.sync="imageDialogVisible"
      :width="dialogWidth"
      custom-class="image-dialog"
    >
      <div class="image-preview">
        <img :src="getProductImage(currentProduct)" :alt="currentProduct.name" />
      </div>
    </el-dialog>

    <!-- 底部提示 -->
    <footer class="page-footer">
      <h4>存储提醒</h4>
      <p>请存放至干燥、阴凉、远离火、儿童不能接触的地方！</p>
      <h4>注意事项</h4>
      <ul>
        <li>燃放前请仔细阅读燃放说明，摆放平稳牢固</li>
        <li>选择宽敞、安全的场地，避开易燃物</li>
        <li>出现异常情况不要马上靠近</li>
        <li>不对未点燃的烟花进行二次点燃</li>
        <li>儿童燃放需家长陪同</li>
      </ul>
    </footer>

    <!-- 回到顶部 -->
    <div class="back-to-top" v-show="showBackTop" @click="scrollToTop">
      <i class="el-icon-arrow-up"></i>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

// 默认占位图
const DEFAULT_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3QgZmlsbD0iIzJhMmE0MCIgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiLz48dGV4dCBmaWxsPSIjZmY2YjZiIiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgeD0iNTAlIiB5PSI1MCUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj7ng5/oirE8L3RleHQ+PC9zdmc+';

export default {
  name: 'Fireworks',
  data() {
    return {
      shopInfo: {
        name: '2026年盛婷烟花爆竹店',
        description: '',
        address: '',
        phone1: '',
        phone2: ''
      },
      products: [],
      currentCategory: '',
      searchKeyword: '',
      videoDialogVisible: false,
      imageDialogVisible: false,
      currentProduct: {},
      windowWidth: window.innerWidth,
      showBackTop: false,
      loading: true
    };
  },
  computed: {
    categories() {
      return [...new Set(this.products.map(p => p.category))];
    },
    filteredProducts() {
      let result = this.products;

      // 分类筛选
      if (this.currentCategory) {
        result = result.filter(p => p.category === this.currentCategory);
      }

      // 搜索筛选
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase();
        result = result.filter(p =>
          p.name.toLowerCase().includes(keyword) ||
          p.category.toLowerCase().includes(keyword)
        );
      }

      return result;
    },
    isMobile() {
      return this.windowWidth < 768;
    },
    dialogWidth() {
      return this.isMobile ? '95%' : '70%';
    }
  },
  async created() {
    await this.loadData();
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    // 加载数据
    async loadData() {
      try {
        const response = await axios.get('/json/fireworks.json');
        const data = response.data;
        this.shopInfo = data.shopInfo;
        this.products = data.products;
        this.loading = false;
      } catch (error) {
        console.error('加载数据失败:', error);
        this.$message.error('加载数据失败，请刷新重试');
      }
    },

    // 获取产品图片
    getProductImage(item) {
      if (item.image) {
        return item.image;
      }
      return DEFAULT_IMAGE;
    },

    // 图片加载失败处理
    handleImageError(event) {
      event.target.src = DEFAULT_IMAGE;
    },

    // 判断是否有视频
    hasVideo(item) {
      return item.videoUrl && item.videoUrl.trim() !== '';
    },

    // 预览图片
    previewImage(item) {
      if (item.image) {
        this.currentProduct = item;
        this.imageDialogVisible = true;
      }
    },

    // 播放视频
    playVideo(item) {
      this.currentProduct = item;
      this.videoDialogVisible = true;
    },

    // 关闭视频
    closeVideo() {
      this.videoDialogVisible = false;
      this.currentProduct = {};
    },

    // 处理窗口大小变化
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    // 处理滚动
    handleScroll() {
      this.showBackTop = window.scrollY > 300;
    },

    // 滚动到顶部
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },

    // 处理电话点击
    handlePhone(phone) {
      if (!phone) return;

      this.$confirm(`电话: ${phone}`, '选择操作', {
        confirmButtonText: '拨打电话',
        cancelButtonText: '复制号码',
        distinguishCancelAndClose: true,
        type: 'info'
      }).then(() => {
        // 拨打电话
        window.location.href = `tel:${phone}`;
      }).catch((action) => {
        if (action === 'cancel') {
          // 复制号码
          this.copyToClipboard(phone);
        }
      });
    },

    // 复制到剪贴板
    copyToClipboard(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('电话号码已复制');
        }).catch(() => {
          this.fallbackCopy(text);
        });
      } else {
        this.fallbackCopy(text);
      }
    },

    // 备用复制方法
    fallbackCopy(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.$message.success('电话号码已复制');
    }
  }
};
</script>

<style scoped>
.fireworks-page {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8f4f8 50%, #fef6f0 100%);
  min-height: 100vh;
  padding: 0 0 20px 0;
  color: #4a5568;
}

.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  padding: 20px 15px;
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.page-header h1 {
  font-size: 20px;
  margin: 0 0 8px 0;
  font-weight: bold;
  color: #fff;
}

.page-header .shop-desc {
  font-size: 12px;
  margin: 0 0 8px 0;
  opacity: 0.95;
  color: #fff;
}

.page-header .contact {
  font-size: 14px;
  margin: 0 0 4px 0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.phone-number {
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  transition: all 0.3s;
}

.phone-number:hover {
  background: rgba(255, 255, 255, 0.35);
  transform: scale(1.05);
}

.phone-number:active {
  transform: scale(0.98);
}

.phone-divider {
  margin: 0 4px;
  opacity: 0.7;
}

.page-header .address {
  font-size: 12px;
  margin: 0;
  opacity: 0.9;
  color: #fff;
}

.category-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 15px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 110px;
  z-index: 99;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.filter-item {
  padding: 6px 14px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  color: #6c757d;
  border: 1px solid #e9ecef;
}

.filter-item.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.filter-item:hover {
  background: linear-gradient(135deg, #a8c0ff 0%, #c4b5fd 100%);
  color: #fff;
  border-color: transparent;
}

.search-box {
  padding: 10px 15px;
}

.search-box ::v-deep .el-input__inner {
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  border-radius: 20px;
}

.search-box ::v-deep .el-input__inner::placeholder {
  color: #a0aec0;
}

.search-box ::v-deep .el-input__inner:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.product-count {
  padding: 5px 15px 10px;
  font-size: 13px;
  color: #718096;
}

.product-count span {
  color: #667eea;
  font-weight: bold;
}

.product-list {
  padding: 0 15px;
}

.product-card {
  background: #fff;
  border-radius: 16px;
  margin-bottom: 12px;
  display: flex;
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

.product-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(102, 126, 234, 0.15);
}

.product-image {
  width: 100px;
  min-width: 100px;
  height: 100px;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(102, 126, 234, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.product-image:hover .image-overlay {
  opacity: 1;
}

.image-overlay i {
  font-size: 24px;
  color: #fff;
}

.product-info {
  flex: 1;
  padding: 12px;
  min-width: 0;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.product-category {
  font-size: 10px;
  background: linear-gradient(135deg, #e0e7ff 0%, #fce7f3 100%);
  padding: 2px 10px;
  border-radius: 10px;
  color: #7c3aed;
}

.product-price {
  font-size: 18px;
  font-weight: bold;
  color: #f472b6;
}

.product-name {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #2d3748;
}

.product-spec {
  font-size: 12px;
  color: #718096;
  margin: 0 0 4px 0;
}

.product-remark {
  font-size: 11px;
  color: #f472b6;
  margin: 0 0 8px 0;
}

.product-actions {
  margin-top: 8px;
}

.no-video {
  font-size: 11px;
  color: #a0aec0;
}

.video-container {
  width: 100%;
  height: 70vh;
  position: relative;
  background: #1a1a2e;
  border-radius: 8px;
  overflow: hidden;
}

.video-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.image-preview {
  text-align: center;
}

.image-preview img {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 8px;
}

.page-footer {
  margin: 20px 15px;
  padding: 20px;
  background: #fff;
  border-radius: 16px;
  border-left: 4px solid #667eea;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.page-footer h4 {
  color: #667eea;
  margin: 0 0 10px 0;
  font-size: 14px;
}

.page-footer p {
  font-size: 13px;
  color: #4a5568;
  margin: 0 0 15px 0;
}

.page-footer ul {
  margin: 0;
  padding-left: 20px;
}

.page-footer li {
  font-size: 12px;
  color: #718096;
  margin-bottom: 6px;
  line-height: 1.5;
}

.back-to-top {
  position: fixed;
  bottom: 80px;
  right: 20px;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  z-index: 100;
  transition: transform 0.3s;
}

.back-to-top:hover {
  transform: scale(1.1);
}

.back-to-top i {
  font-size: 20px;
  color: #fff;
}

/* 弹窗样式 */
::v-deep .video-dialog,
::v-deep .image-dialog {
  background: #fff;
  border-radius: 16px;
}

::v-deep .video-dialog .el-dialog__header,
::v-deep .image-dialog .el-dialog__header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px 16px 0 0;
  padding: 15px 20px;
}

::v-deep .video-dialog .el-dialog__title,
::v-deep .image-dialog .el-dialog__title {
  color: #fff;
  font-size: 16px;
}

::v-deep .video-dialog .el-dialog__headerbtn .el-dialog__close,
::v-deep .image-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #fff;
}

::v-deep .video-dialog .el-dialog__body,
::v-deep .image-dialog .el-dialog__body {
  padding: 15px;
  background: #fff;
}

/* 按钮样式优化 */
::v-deep .el-button--primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 20px;
}

::v-deep .el-button--primary:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

/* 手机端适配 */
@media (max-width: 480px) {
  .product-image {
    width: 80px;
    min-width: 80px;
    height: 80px;
  }

  .product-name {
    font-size: 14px;
  }

  .product-price {
    font-size: 16px;
  }

  .category-filter {
    top: 100px;
  }
}
</style>
